// src/components/PinGate/PinGate.tsx
import React from 'react'
import { Button, Card, Spinner } from 'react-bootstrap'
import { dbRead, dbUpdate, sha256Hex } from '../../_services/firebase'
import { saveLog } from '../../_services/firebase'
import { safeNum, safeStr, toastDanger } from '../../_utils'
import type { PinSafeDoc, PinDangerDoc } from '../../_types'
import { DB_PIN_SAFE_BY_ID, DB_PIN_DANGER_BY_ID } from '../../_config/db_paths'

const PIN_LEN = 6

function lockMinutes(failCount: number): number {
  if (failCount <= 3) return 0
  return 5 * Math.pow(2, failCount - 3)
}

function pad2(n: number) {
  return String(Math.max(0, Math.floor(n))).padStart(2, '0')
}

// ─── PinDots ─────────────────────────────────────────────────
function PinDots({ value }: { value: string }) {
  return (
    <div className='d-flex justify-content-center gap-2 mb-3'>
      {Array.from({ length: PIN_LEN }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            border: '2px solid #aaa',
            background: i < value.length ? '#222' : 'transparent',
            transition: 'background 0.15s',
          }}
        />
      ))}
    </div>
  )
}

// ─── Keypad ───────────────────────────────────────────────────
function Keypad({
  onDigit, onBack, onClear, disabled,
}: {
  onDigit: (d: string) => void
  onBack: () => void
  onClear: () => void
  disabled?: boolean
}) {
  const rows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['ล้าง', '0', 'ลบ'],
  ]
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,72px)', gap: 10, margin: '0 auto', width: 'fit-content' }}>
      {rows.flat().map((k) => (
        <Button
          key={k}
          variant={k === 'ลบ' || k === 'ล้าง' ? 'outline-secondary' : 'outline-dark'}
          disabled={disabled}
          style={{ height: 60, width: 72, fontSize: 15, fontWeight: 600 }}
          onClick={() => {
            if (k === 'ลบ') onBack()
            else if (k === 'ล้าง') onClear()
            else onDigit(k)
          }}
        >
          {k}
        </Button>
      ))}
    </div>
  )
}

// ─── PinGate ──────────────────────────────────────────────────
export default function PinGate({ uid, onPassed }: { uid: string; onPassed: () => void }) {
  const [loading, setLoading]       = React.useState(true)
  const [pinSafe, setPinSafe]       = React.useState<PinSafeDoc | null>(null)
  const [pinDanger, setPinDanger]   = React.useState<PinDangerDoc | null>(null)
  const [mode, setMode]             = React.useState<'set' | 'enter'>('enter')
  const [pin, setPin]               = React.useState('')
  const [pin2, setPin2]             = React.useState('')
  const [saving, setSaving]         = React.useState(false)
  const [, forceRender]             = React.useState(0)

  const now        = Date.now()
  const lockedUntil = safeNum(pinDanger?.lockedUntil)
  const isLocked   = lockedUntil > now
  const remainSec  = isLocked ? Math.ceil((lockedUntil - now) / 1000) : 0

  // countdown timer
  React.useEffect(() => {
    if (!isLocked) return
    const t = setInterval(() => forceRender((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [isLocked])

  // load PIN data
  const reload = React.useCallback(async () => {
    const [safe, danger] = await Promise.all([
      dbRead<PinSafeDoc>(DB_PIN_SAFE_BY_ID(uid)),
      dbRead<PinDangerDoc>(DB_PIN_DANGER_BY_ID(uid)),
    ])
    setPinSafe(safe)
    setPinDanger(danger)
    setMode(!safe?.hash ? 'set' : 'enter')
  }, [uid])

  React.useEffect(() => {
    ;(async () => {
      try { await reload() } catch (e: any) { toastDanger(e?.message || 'โหลด PIN ไม่สำเร็จ') }
      finally { setLoading(false) }
    })()
  }, [reload])

  // keyboard support (PC)
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (saving || isLocked) return
      if (/^[0-9]$/.test(e.key)) pushDigit(e.key)
      else if (e.key === 'Backspace') backspace()
      else if (e.key === 'Escape') clear()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, isLocked, pin, pin2, mode])

  const pushDigit = (d: string) => {
    if (saving || isLocked) return
    if (mode === 'set') {
      if (pin.length < PIN_LEN) setPin((p) => p + d)
      else if (pin2.length < PIN_LEN) setPin2((p) => p + d)
    } else {
      if (pin.length < PIN_LEN) setPin((p) => p + d)
    }
  }

  const backspace = () => {
    if (saving || isLocked) return
    if (mode === 'set') {
      if (pin2.length > 0) setPin2((p) => p.slice(0, -1))
      else setPin((p) => p.slice(0, -1))
    } else {
      setPin((p) => p.slice(0, -1))
    }
  }

  const clear = () => { setPin(''); setPin2('') }

  const resetFail = async () => {
    await dbUpdate(DB_PIN_DANGER_BY_ID(uid), { failCount: 0, lockedUntil: 0, updatedAt: Date.now() })
  }

  const incFail = async (current: number) => {
    const next  = current + 1
    const mins  = lockMinutes(next)
    const until = mins > 0 ? Date.now() + mins * 60_000 : 0
    const patch = { failCount: next, lockedUntil: until, lastFailAt: Date.now(), updatedAt: Date.now() }
    await dbUpdate(DB_PIN_DANGER_BY_ID(uid), patch)
    setPinDanger((p) => ({ ...(p || {}), ...patch }))
    await saveLog(uid, 'pin_fail', uid, `ครั้งที่ ${next}`)
    return { next, mins }
  }

  // auto-submit
  React.useEffect(() => {
    if (mode === 'enter' && pin.length === PIN_LEN) submitEnter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, mode])

  React.useEffect(() => {
    if (mode === 'set' && pin.length === PIN_LEN && pin2.length === PIN_LEN) submitSet()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, pin2, mode])

  const submitEnter = async () => {
    if (saving || isLocked) return
    const stored = safeStr(pinSafe?.hash)
    if (!stored) { setMode('set'); setPin(''); return }
    setSaving(true)
    try {
      const hash = await sha256Hex(`${uid}:${pin}`)
      if (hash === stored) {
        await resetFail()
        setPin('')
        onPassed()
        return
      }
      const { next, mins } = await incFail(safeNum(pinDanger?.failCount))
      setPin('')
      toastDanger(mins > 0
        ? `PIN ไม่ถูกต้อง (ผิด ${next} ครั้ง) ระบบล็อค ${mins} นาที`
        : `PIN ไม่ถูกต้อง (ผิดแล้ว ${next} ครั้ง)`
      )
    } catch (e: any) {
      toastDanger(e?.message || 'ตรวจสอบ PIN ไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const submitSet = async () => {
    if (saving || isLocked) return
    if (pin !== pin2) {
      setPin(''); setPin2('')
      toastDanger('PIN ไม่ตรงกัน กรุณาตั้งใหม่')
      return
    }
    setSaving(true)
    try {
      const hash = await sha256Hex(`${uid}:${pin}`)
      await dbUpdate(DB_PIN_SAFE_BY_ID(uid), {
        hash,
        createdAt: pinSafe?.createdAt || Date.now(),
        updatedAt: Date.now(),
      })
      await resetFail()
      await saveLog(uid, 'pin_set', uid)
      setPin(''); setPin2('')
      setMode('enter')
      onPassed()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึก PIN ไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '100vh' }}>
        <div className='text-center'>
          <Spinner animation='border' variant='warning' />
          <div className='mt-2 text-muted' style={{ fontSize: 13 }}>กำลังเตรียมระบบ PIN...</div>
        </div>
      </div>
    )
  }

  const dotValue  = mode === 'set' ? (pin.length < PIN_LEN ? pin : pin2) : pin
  const title     = mode === 'set' ? 'ตั้งค่า PIN (6 หลัก)' : 'กรอก PIN เพื่อเข้าใช้งาน'
  const helpText  = isLocked
    ? `ระบบล็อคชั่วคราว เหลือ ${pad2(remainSec / 60)}:${pad2(remainSec % 60)}`
    : mode === 'set'
      ? pin.length < PIN_LEN ? 'กรอก PIN ใหม่ 6 หลัก' : 'ยืนยัน PIN อีกครั้ง'
      : 'กรอก PIN 6 หลัก'

  return (
    <div
      className='d-flex justify-content-center align-items-center'
      style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}
    >
      <Card className='shadow-sm' style={{ width: 340 }}>
        <Card.Body className='text-center p-4'>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{title}</div>
          <div className='text-muted mt-1 mb-4' style={{ fontSize: 13 }}>{helpText}</div>

          <PinDots value={dotValue} />

          <div className='mt-3'>
            <Keypad
              onDigit={pushDigit}
              onBack={backspace}
              onClear={clear}
              disabled={!(!saving && !isLocked)}
            />
          </div>

          {mode === 'enter' && (
            <div className='mt-3 text-muted' style={{ fontSize: 12 }}>
              ผิดไปแล้ว: <strong>{safeNum(pinDanger?.failCount)}</strong> ครั้ง
            </div>
          )}

          {saving && (
            <div className='mt-2'>
              <Spinner size='sm' animation='border' variant='warning' />
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}